import React, { useState } from 'react';
import supabase from '../../../supabase/supabase';
import { FiShield, FiCheck, FiAlertTriangle, FiX } from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const SystemAudit = () => {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);

  const runAudit = async () => {
    setRunning(true);
    const checks = [];
    
    // 1. Gateway Config
    const { data: gw } = await supabase.from('payment_gateways').select('*').eq('name', 'marx_ipg').single();
    checks.push({ name: 'Gateway Configuration', status: gw ? 'PASS' : 'FAIL', desc: 'Gateway record exists in database.' });
    
    // 2. Tables Exist
    const tables = ['payment_transactions', 'payment_logs', 'payment_webhooks'];
    for (const t of tables) {
      const { error } = await supabase.from(t).select('id').limit(1);
      checks.push({ name: `Table: ${t}`, status: error?.code === '42P01' ? 'FAIL' : 'PASS', desc: `Required table ${t} exists.` });
    }

    // 3. Test Connection
    try {
      const { data } = await supabase.functions.invoke('marx-test-connection');
      checks.push({ name: 'API Connectivity', status: data?.success ? 'PASS' : 'FAIL', desc: 'Secure connection to Marx IPG established.' });
    } catch {
      checks.push({ name: 'API Connectivity', status: 'FAIL', desc: 'Edge function unreachable or network error.' });
    }

    setResults(checks);
    setRunning(false);
  };

  const isReady = results && results.every(r => r.status === 'PASS');

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-serif text-gray-800">System Audit & Production Readiness</h3>
            <p className="text-xs text-gray-500">Validates configuration, database integrity, and edge functions.</p>
          </div>
          <button onClick={runAudit} disabled={running} className="bg-brand-darkSage text-white px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-brand-gold transition-colors flex items-center gap-2">
            <SafeIcon icon={FiShield} /> {running ? 'Auditing...' : 'Run Full Payment Audit'}
          </button>
        </div>

        {results && (
          <div className="space-y-3">
            {results.map((r, i) => (
              <div key={i} className={`p-4 rounded-xl border flex items-start gap-4 ${r.status === 'PASS' ? 'bg-green-50 border-green-100' : r.status === 'WARN' ? 'bg-yellow-50 border-yellow-100' : 'bg-red-50 border-red-100'}`}>
                <SafeIcon icon={r.status === 'PASS' ? FiCheck : r.status === 'WARN' ? FiAlertTriangle : FiX} className={`mt-0.5 ${r.status === 'PASS' ? 'text-green-600' : r.status === 'WARN' ? 'text-yellow-600' : 'text-red-600'}`} />
                <div>
                  <h4 className="font-bold text-sm text-gray-800">{r.name}</h4>
                  <p className="text-xs text-gray-600">{r.desc}</p>
                </div>
                <div className="ml-auto">
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${r.status === 'PASS' ? 'bg-green-200 text-green-800' : r.status === 'WARN' ? 'bg-yellow-200 text-yellow-800' : 'bg-red-200 text-red-800'}`}>
                    {r.status}
                  </span>
                </div>
              </div>
            ))}

            <div className={`mt-8 p-6 rounded-2xl text-center border-2 ${isReady ? 'bg-green-50 border-green-400' : 'bg-gray-50 border-gray-200'}`}>
              <h2 className={`text-2xl font-serif font-bold ${isReady ? 'text-green-700' : 'text-gray-500'}`}>
                {isReady ? 'PRODUCTION READY' : 'SYSTEM NOT READY'}
              </h2>
              <p className="text-sm mt-2 text-gray-600">
                {isReady ? 'All diagnostic checks passed. The payment infrastructure complies with official Marx IPG v4 documentation.' : 'Resolve the failing checks above before enabling production traffic.'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemAudit;